import React, { useState } from 'react';

import { useSelector } from 'react-redux';

import DashboardTabs from './components/DashboardTabs';
import DashboardSampleBanner from './components/DashboardSampleBanner';
import ContactsTab from './components/Contacts/ContactsTab';
import EngagementTab from './components/Engagement/EngagementTab';
import MailTrackingTab from './components/MailTracking/MailTrackingTab';
import GettingStarted from './GettingStarted';
import useDashboardData from './hooks/useDashboardData';
import useDashboardDateRange from './hooks/useDashboardDateRange';
import useDashboardTabs from './hooks/useDashboardTabs';
import { GETTING_STARTED_HIDDEN_STORAGE_KEY } from './constants';
import { getIsGettingStartedHidden } from './utils';

import './styles.scss';

const Dashboard = () => {
  const dashboardWidgets = useSelector((state) => state.dashboardReducers.widgets);
  const dashboardContacts = useSelector((state) => state.dashboardReducers.contacts);
  const dashboardEngagement = useSelector((state) => state.dashboardReducers.engagement);
  const isLiveMode = useSelector((state) => state.userReducers.user?.isLiveMode === true);
  const [isGettingStartedHidden, setIsGettingStartedHidden] = useState(() =>
    getIsGettingStartedHidden(GETTING_STARTED_HIDDEN_STORAGE_KEY)
  );
  const { activeTab, setActiveTab } = useDashboardTabs();
  const dateRange = useDashboardDateRange();
  const dashboardData = useDashboardData({
    dashboardWidgets,
    dashboardContacts,
    dashboardEngagement,
    duration: dateRange.dashboardDuration,
  });

  const hideGettingStarted = () => {
    setIsGettingStartedHidden(true);

    try {
      window.localStorage.setItem(GETTING_STARTED_HIDDEN_STORAGE_KEY, 'true');
    } catch {
      // Keep the in-memory preference when browser storage is unavailable.
    }
  };

  const showGettingStarted = () => {
    setIsGettingStartedHidden(false);

    try {
      window.localStorage.removeItem(GETTING_STARTED_HIDDEN_STORAGE_KEY);
    } catch {
      // The guide can still be reopened when browser storage is unavailable.
    }
  };

  const gettingStarted = dashboardWidgets?.gettingStarted || {};
  const hasAddedContacts = gettingStarted.contactsAdded === true;
  const hasCreatedMailPiece = gettingStarted.mailPieceCreated === true;
  const hasPlacedOrder = gettingStarted.orderPlaced === true;
  const isSetupGuideVisible = !isGettingStartedHidden;

  const isActiveTabSampleData = () => {
    switch (activeTab) {
      case 'contacts':
        return dashboardData.contactsOverview?.isSampleData === true;
      case 'engagement':
        return dashboardData.engagementOverview?.isSampleData === true;
      case 'mail':
      default:
        return (
          dashboardData.dashboardWidgets?.isSampleData === true
          || dashboardData.dashboardWidgets?.mailPerformance?.isSampleData === true
        );
    }
  };

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'contacts':
        return (
          <ContactsTab
            contacts={dashboardData.contactsOverview}
            isRefreshing={dashboardData.isRefreshing}
            onRefresh={dashboardData.refreshDashboard}
          />
        );
      case 'engagement':
        return (
          <EngagementTab
            engagement={dashboardData.engagementOverview}
            isLoading={dashboardData.isLoading}
            isRefreshing={dashboardData.isRefreshing}
            dateRange={dateRange}
            onRefresh={dashboardData.refreshDashboard}
          />
        );
      case 'mail':
      default:
        return (
          <MailTrackingTab
            dashboardWidgets={dashboardData.dashboardWidgets}
            isLoading={dashboardData.isLoading}
            isRefreshing={dashboardData.isRefreshing}
            dateRange={dateRange}
            onRefresh={dashboardData.refreshDashboard}
          />
        );
    }
  };

  return (
    <main
      className="mail-performance-dashboard dashboard-v2"
      aria-busy={dashboardData.isLoading || dashboardData.isRefreshing}
    >
      {isSetupGuideVisible ? (
        dashboardData.isLoading && !dashboardData.hadCachedDashboardDataOnMount ? (
          <section
            className="getting-started-card dashboard-getting-started-loading"
            aria-label="Loading setup guide"
            aria-busy="true"
          >
            <span className="dashboard-loading-line dashboard-loading-line--heading" />
            <span className="dashboard-loading-line dashboard-loading-line--copy" />
            <div className="dashboard-loading-guide-grid">
              <span />
              <span />
            </div>
          </section>
        ) : (
          <GettingStarted
            hasAddedContacts={hasAddedContacts}
            hasCreatedMailPiece={hasCreatedMailPiece}
            hasPlacedOrder={hasPlacedOrder}
            isLiveMode={isLiveMode}
            onHide={hideGettingStarted}
          />
        )
      ) : null}

      <div className="dashboard-tabs-region">
        <DashboardTabs
          activeTab={activeTab}
          onChange={setActiveTab}
          isSetupGuideOpen={isSetupGuideVisible}
          onSetupGuideToggle={isSetupGuideVisible ? hideGettingStarted : showGettingStarted}
        />
      </div>

      {isActiveTabSampleData() ? <DashboardSampleBanner /> : null}

      {renderActiveTab()}
    </main>
  );
};

export default Dashboard;
